import React, { useState } from 'react';
import { formatDate } from '../utils/dateConverter';
import {
    PRIORITY_LABEL,
    STATUSES,
    STATUS_LABEL,
    columnHours,
    groupByStatus,
} from '../utils/planning';

const initials = (name) => (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0].toUpperCase())
    .join('');

const fmtHours = (hours) => {
    const value = Number(hours) || 0;
    return Number.isInteger(value) ? `${value} h` : `${value.toFixed(1).replace('.', ',')} h`;
};

/** The status board; dragging a card calls `onMove(task, status)`. */
export default function PlanningBoard({ tasks, onMove, onCardClick, busy = false }) {
    const [dragId, setDragId] = useState(null);
    const [overColumn, setOverColumn] = useState(null);
    const columns = groupByStatus(tasks);
    const hours = columnHours(tasks);

    // The dragged id travels in the dataTransfer, not in React state: a state update is
    // not committed by the time onDrop runs, so reading it there finds the previous card.
    const handleDragStart = (event, task) => {
        setDragId(String(task.id));
        if (event.dataTransfer) {
            event.dataTransfer.setData('text/plain', String(task.id));
            event.dataTransfer.effectAllowed = 'move';
        }
    };

    const handleDrop = (event, status) => {
        setOverColumn(null);
        const carried = event.dataTransfer?.getData('text/plain') || dragId;
        const task = (tasks || []).find(t => String(t.id) === String(carried));
        setDragId(null);
        if (task && task.status !== status) onMove(task, status);
    };

    return (
        <div className="pl-board">
            {STATUSES.map(status => (
                <section
                    key={status}
                    className={`pl-column${overColumn === status ? ' pl-column--over' : ''}`}
                    onDragOver={e => { e.preventDefault(); setOverColumn(status); }}
                    onDragLeave={() => setOverColumn(cur => (cur === status ? null : cur))}
                    onDrop={e => handleDrop(e, status)}
                    data-testid={`column-${status}`}
                >
                    <header className="pl-column-head">
                        <span className={`pl-column-title pl-status--${status}`}>
                            {STATUS_LABEL[status]}
                        </span>
                        <span className="pl-column-count">{columns[status].length}</span>
                        {hours[status] > 0 && (
                            <span className="pl-column-hours">{fmtHours(hours[status])}</span>
                        )}
                    </header>

                    <div className="pl-column-body">
                        {columns[status].length === 0 && (
                            <p className="pl-column-empty">Keine Aufgaben</p>
                        )}
                        {columns[status].map(task => (
                            <article
                                key={task.id}
                                className={`pl-card${dragId === String(task.id) ? ' pl-card--dragging' : ''}`}
                                draggable={!busy}
                                onDragStart={e => handleDragStart(e, task)}
                                onDragEnd={() => setDragId(null)}
                                onClick={() => onCardClick(task)}
                                data-testid={`task-${task.id}`}
                            >
                                <div className="pl-card-top">
                                    {task.is_milestone && (
                                        <span className="pl-milestone" title="Meilenstein">◆</span>
                                    )}
                                    <h4 className="pl-card-title">{task.title || 'Ohne Titel'}</h4>
                                </div>

                                <div className="pl-card-meta">
                                    {task.assignee_name ? (
                                        <span
                                            className="pl-avatar"
                                            title={`Zuständig: ${task.assignee_name}`}
                                        >
                                            {initials(task.assignee_name)}
                                        </span>
                                    ) : (
                                        <span className="pl-avatar pl-avatar--none" title="Niemand zugewiesen">
                                            –
                                        </span>
                                    )}
                                    {task.estimate_hours > 0 && (
                                        <span className="pl-card-hours">{fmtHours(task.estimate_hours)}</span>
                                    )}
                                    {task.priority && task.priority !== 'Medium' && (
                                        <span className={`pl-prio pl-prio--${task.priority.toLowerCase()}`}>
                                            {PRIORITY_LABEL[task.priority] || task.priority}
                                        </span>
                                    )}
                                </div>

                                {task.planned_end && (
                                    <div className="pl-card-date">
                                        bis {formatDate(task.planned_end)}
                                    </div>
                                )}
                            </article>
                        ))}
                    </div>
                </section>
            ))}
        </div>
    );
}
